import clsx from "clsx";
import NextImage, {
  type ImageProps,
  type StaticImageData,
} from "next/image";


export type ImageSrc = SvgImageSrc | NonSvgImageSrc;
export type SvgImageSrc = `${string}.svg`;
export type NonSvgImageSrc = StaticImageData | Exclude<string, SvgImageSrc> /* does not have effect, just for clarity */;

export type StrictImageProps = SvgImageProps | NonSvgImageProps;

type SvgImageProps = Omit<ImageProps, "src"> & {
  src: SvgImageSrc;
};

type NonSvgImageProps = (
  & Omit<ImageProps, "src" | "fill" | "width" | "height" | "sizes">
  & { src: NonSvgImageSrc }

  /* TODO make `sizes` not optional for strictness */
  & { sizes?: NonNullable<ImageProps["sizes"]> }

  & (
    | { width: NonNullable<ImageProps["width"]>; height?: ImageProps["height"]; fill?: never }
    | { height: NonNullable<ImageProps["height"]>; width?: ImageProps["width"]; fill?: never }
    | { fill: true; width?: ImageProps["width"]; height?: ImageProps["height"]; }

    /* TODO remove to ensure strictness */
    | { fill?: boolean; width?: ImageProps["width"]; height?: ImageProps["height"]; }
  )
);


export function Image(props: StrictImageProps) {
  const normalizedProps = {
    ...props,
    sizes: props.sizes ?? "100vw",
  };

  return (
    <NextImage {...normalizedProps} />
  );
}


type ResponsiveImageProps = Omit<ImageProps, "fill"> & {
  rounded?: boolean;
  aspect?: string; // e.g. "aspect-[4/3]" or "aspect-square"
  fill?: boolean;
};

/** TODO */
export const ResponsiveImage = ({
  fill,
  width,
  height,
  sizes,
  placeholder = "blur",
  className,
  style,
  rounded = true,
  aspect,
  ...rest
}: ResponsiveImageProps) => {
  const shouldUseFill =
    fill || (!width && !height) || (!width && !!height);

  if (shouldUseFill) {
    const wrapperClass = clsx(
      "relative",
      height ? `h-[${height}px]` : "",
      width ? `w-[${width}px]` : "w-full",
      aspect || (!height && !width && "aspect-video"),
      rounded && "rounded-md",
      className
    );

    return (
      <div className={wrapperClass} style={style}>
        <NextImage
          {...rest}
          fill
          sizes={sizes}
          placeholder={rest.blurDataURL ? placeholder : "empty"}
          className={clsx("object-cover", className)}
        />
      </div>
    );
  }

  // fixed-size layout (width, optional height)
  return (
    <NextImage
      {...rest}
      width={width}
      height={height}
      sizes={sizes}
      placeholder={rest.blurDataURL ? placeholder : "empty"}
      className={clsx(rounded && "rounded-md", className)}
      style={style}
    />
  );
};
